import jsPDF from "jspdf";

const MARGIN = 14;
const TOP_MARGIN = 14;
const BOTTOM_MARGIN = 12;
const GROUP_GAP = 2;
const GROUP_HEIGHT = 8;
const MIN_ROW_HEIGHT = 18;
const LABEL_STEP = 3.8;
const VALUE_STEP = 4.6;
const CELL_BASE_HEIGHT = 14;

const wrap = (doc, value, width) => {
  const lines = doc.splitTextToSize(String(value ?? ""), Math.max(3, width));
  return Array.isArray(lines) && lines.length ? lines : [""];
};

const attachmentName = (reference) => {
  let path = String(reference);
  try {
    const url = new URL(path, "https://local.invalid");
    path = url.searchParams.get("path") || url.pathname;
    path = decodeURIComponent(path);
  } catch { /* A legacy reference can contain malformed percent encoding. */ }
  const name = path.split(/[\\/]/).filter(Boolean).pop() || "";
  // Old uploads can retain only an opaque storage name, not the original name.
  const stem = name.replace(/\.[^.]+$/, "");
  const isPath = /[\\/]/.test(String(reference));
  const hasExtension = /\.[^./\\]+$/.test(name);
  return !name || (isPath && !hasExtension) || /^(?:[a-f0-9]{32,}|[a-f0-9]{8}(?:-[a-f0-9]{4}){3}-[a-f0-9]{12}|\d{13}-[a-f0-9]+)$/i.test(stem)
    ? "Arquivo anexado" : name;
};

const FILE_TYPES = new Set(["file", "files", "attachment", "attachments", "upload", "image", "photo", "signature-attachment"]);
const SIGNATURE_DESCRIPTIONS = {
  COMPLETED: "Documento assinado",
  PENDING: "Aguardando assinatura",
  IN_PROGRESS: "Assinatura em andamento",
  DRAFT: "Assinatura em preparação",
  DECLINED: "Assinatura recusada",
  CANCELED: "Assinatura cancelada",
  CANCELLED: "Assinatura cancelada",
  EXPIRED: "Prazo de assinatura encerrado",
};

export const printableValue = (input, field = {}) => {
  if (input == null || input?.__removed === true) return "";
  if (typeof input === "boolean") return input ? "Sim" : "Não";
  if (Array.isArray(input)) {
    return input.map(value => printableValue(value, field)).filter(Boolean).join("\n");
  }
  if (typeof input === "string" && /^data:/i.test(input)) {
    const mime = input.match(/^data:([^;,]+)/i)?.[1] || "";
    const extension = { "image/jpeg": "jpg", "image/png": "png", "image/webp": "webp", "image/gif": "gif", "application/pdf": "pdf" }[mime];
    return `${field.label || "Arquivo"}${extension ? `.${extension}` : ""}`;
  }
  if (typeof input === "string" && FILE_TYPES.has(field.type)) {
    return attachmentName(input, field);
  }
  if (input && typeof input === "object" && input.requestId &&
    (input.status || input.finalHash || input.verificationCode)) {
    const name = input.originalName || input.originalname || input.fileName || input.filename || input.name;
    if (typeof name === "string" && name.trim()) return name;
    return SIGNATURE_DESCRIPTIONS[String(input.status || "").toUpperCase()] || "Documento para assinatura";
  }
  if (input && typeof input === "object" && Array.isArray(input.files)) {
    return printableValue(input.files, field);
  }
  if (field.type === "table" && field.tableColumns?.length && input && typeof input === "object") {
    return (field.tableColumns || []).map(column => {
      const text = printableValue(input[column.id], column);
      return text ? `${column.label || column.id}: ${text}` : "";
    }).filter(Boolean).join("\n");
  }
  let value = input;
  if (value && typeof value === "object") {
    if (Array.isArray(value.selected) && Array.isArray(value.options)) {
      const labelsById = new Map();
      value.options.forEach((option) => {
        if (!option) return;
        if (typeof option === "string") {
          labelsById.set(option, option);
          return;
        }
        const key = option.id ?? option.value ?? option.label ?? option.name;
        if (key == null) return;
        labelsById.set(String(key), String(option.label ?? option.name ?? option.value ?? key));
      });
      const selected = value.selected
        .map((key) => labelsById.get(String(key)) || String(key))
        .filter((label) => label.trim().length);
      value = selected.length
        ? selected.join(", ")
        : labelsById.size ? "Opções: " + [...labelsById.values()].join(", ") : "";
    } else if (value.originalName || value.originalname || value.fileName || value.filename || value.name) {
      return String(value.originalName || value.originalname || value.fileName || value.filename || value.name);
    } else if (value.dataUrl) {
      return printableValue(value.dataUrl, field);
    } else if (value.relativePath || value.downloadPath || value.downloadUrl || value.url) {
      const reference = value.relativePath || value.downloadPath || value.downloadUrl || value.url;
      return attachmentName(reference, field);
    } else {
      if (FILE_TYPES.has(field.type)) return "Arquivo anexado";
      // Never serialize arbitrary internal property names into a user document.
      const display = value.label ?? value.displayValue ?? value.title ?? value.value;
      return display != null && typeof display !== "object"
        ? printableValue(display) : "Informação não disponível";
    }
  }
  return String(value ?? "");
};

const fieldSpan = (field) => {
  const requested = Number(field?.size || 12);
  return Number.isFinite(requested) ? Math.min(12, Math.max(1, Math.floor(requested))) : 12;
};

const cellHeight = (labelCount, valueCount) =>
  Math.max(MIN_ROW_HEIGHT, CELL_BASE_HEIGHT +
    Math.max(0, labelCount - 1) * LABEL_STEP +
    Math.max(0, valueCount - 1) * VALUE_STEP);

const prepareRows = (doc, fields, values, columnWidth) => {
  const rows = [];
  let row = [];
  let used = 0;
  for (const field of fields) {
    const span = fieldSpan(field);
    if (row.length && used + span > 12) {
      rows.push(row);
      row = [];
      used = 0;
    }
    const width = span * columnWidth;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    const labels = wrap(doc, field.label || field.id || "", width - 4);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    const lines = wrap(doc, printableValue(values[field.id], field), width - 4);
    row.push({ span, width, labels, lines });
    used += span;
  }
  if (row.length) rows.push(row);
  return rows;
};

/**
 * Renderiza o formulário completo, sem altura fixa de célula e com quebras de
 * página antes de perder qualquer linha. Linhas excepcionalmente longas podem
 * continuar na página seguinte; grupos recebem título de continuação.
 */
export const renderRequestPdf = (doc, { instance, form, values = {} }) => {
  if (!instance) return doc;
  doc.setFont("helvetica", "normal");
  doc.setLineWidth(0.1);
  doc.setLineHeightFactor(1.15);

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const contentWidth = pageWidth - MARGIN * 2;
  const columnWidth = contentWidth / 12;
  const pageBottom = pageHeight - BOTTOM_MARGIN;
  let y = MARGIN;
  let activeGroup = "";

  const groupTitle = (label, continuation = false) => {
    const labelText = continuation ? label + " (continuação)" : label;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    const lines = wrap(doc, labelText, contentWidth - 6);
    const height = Math.max(GROUP_HEIGHT, 3 + lines.length * 4.8);
    doc.setFillColor(241, 245, 250);
    doc.setDrawColor(226, 232, 240);
    doc.roundedRect(MARGIN, y, contentWidth, height, 1.5, 1.5, "F");
    doc.setFillColor(11, 31, 82);
    doc.rect(MARGIN, y + 1, .8, height - 2, "F");
    doc.setTextColor(11, 31, 82);
    doc.setFont("helvetica", "bold");
    doc.text(lines, MARGIN + 3, y + 5);
    doc.setFont("helvetica", "normal");
    y += height;
    return height;
  };

  const nextPage = () => {
    doc.addPage();
    y = TOP_MARGIN;
    if (activeGroup) groupTitle(activeGroup, true);
  };

  const drawRow = (cells) => {
    const fullHeight = Math.max(...cells.map((cell) =>
      cellHeight(cell.labels.length, cell.lines.length)));
    const freshHeight = pageBottom - TOP_MARGIN -
      (activeGroup ? Math.max(GROUP_HEIGHT, 3 +
        (() => {
          doc.setFont("helvetica", "bold");
          doc.setFontSize(11);
          return wrap(doc, activeGroup + " (continuação)", contentWidth - 6).length * 4.8;
        })()) : 0);

    // Preserva as linhas inteiras quando cabem em uma página vazia.
    if (fullHeight <= freshHeight && y + fullHeight > pageBottom) nextPage();

    const cursor = cells.map(() => 0);
    while (true) {
      if (pageBottom - y < MIN_ROW_HEIGHT) nextPage();

      const available = pageBottom - y;
      const chunks = cells.map((cell, index) => {
        const base = CELL_BASE_HEIGHT + Math.max(0, cell.labels.length - 1) * LABEL_STEP;
        const maxLines = Math.max(1, Math.floor((available - base) / VALUE_STEP) + 1);
        const portion = cell.lines.slice(cursor[index], cursor[index] + maxLines);
        return portion;
      });
      const height = Math.max(MIN_ROW_HEIGHT, ...cells.map((cell, i) =>
        cellHeight(cell.labels.length, chunks[i].length)));

      if (height > available + 0.01) {
        // Uma legenda longa pode ocupar mais espaço que o restante da página.
        // A quebra acontece antes de desenhar qualquer conteúdo fora da folha.
        nextPage();
        continue;
      }

      let x = MARGIN;
      cells.forEach((cell, i) => {
        doc.setDrawColor(226, 232, 240);
        doc.setFillColor(255, 255, 255);
        doc.rect(x, y, cell.width, height, "FD");
        doc.setFont("helvetica", "bold");
        doc.setTextColor(100, 116, 139);
        doc.setFontSize(9);
        doc.text(cell.labels, x + 2, y + 5);
        if (chunks[i].length) {
          doc.setFont("helvetica", "normal");
          doc.setTextColor(30, 41, 59);
          doc.setFontSize(11);
          doc.text(chunks[i], x + 2,
            y + 11 + Math.max(0, cell.labels.length - 1) * LABEL_STEP);
        }
        cursor[i] += chunks[i].length;
        x += cell.width;
      });
      y += height;

      if (cells.every((cell, i) => cursor[i] >= cell.lines.length)) return;
      nextPage();
    }
  };

  const processName = String(instance.process?.name || "Processo");
  const headerTitle = "Solicitação " + instance.id + " - " + processName;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  const titleLines = wrap(doc, headerTitle, contentWidth - 12);
  const headerHeight = 12 + titleLines.length * 6;
  doc.setFillColor(11, 31, 82);
  doc.roundedRect(MARGIN, y, contentWidth, headerHeight, 2.5, 2.5, "F");
  doc.setTextColor(255, 255, 255);
  doc.text(titleLines, MARGIN + 6, y + 9);
  y += headerHeight + 6;

  const requester =
    instance.requester?.name || instance.user?.name || instance.startedBy?.name || "-";
  const startedAt = instance.startedAt ? new Date(instance.startedAt) : null;
  const startedText = startedAt && !Number.isNaN(startedAt.getTime())
    ? startedAt.toLocaleString("pt-BR") : "-";
  const metadata = [["Número:", String(instance.id)], ["Processo:", processName], ["Solicitante:", requester], ["Início:", startedText]];
  const metadataWidth = (contentWidth - 4) / 2;
  for (let index = 0; index < metadata.length; index += 2) {
    const cells = metadata.slice(index, index + 2).map(([label, value]) => {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      return { label, lines: wrap(doc, value, metadataWidth - 8) };
    });
    const height = Math.max(...cells.map(cell => 12 + cell.lines.length * 4.6));
    if (y + height > pageBottom) nextPage();
    cells.forEach((cell, cellIndex) => {
      const x = MARGIN + cellIndex * (metadataWidth + 4);
      doc.setFillColor(248, 250, 252);
      doc.roundedRect(x, y, metadataWidth, height, 1.5, 1.5, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text(cell.label, x + 4, y + 5);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(30, 41, 59);
      doc.text(cell.lines, x + 4, y + 11);
    });
    y += height + 3;
  }
  y += 3;

  const groups = Array.isArray(form?.schema?.groups) ? form.schema.groups : [];
  const fields = Array.isArray(form?.schema?.fields) ? form.schema.fields : [];
  const orderedGroups = [...groups, {
    id: "__ungrouped__",
    label: groups.length ? "Campos" : "",
  }];

  for (const group of orderedGroups) {
    const selected = fields.filter((field) =>
      (field.groupId || "__ungrouped__") === (group.id || "__ungrouped__") &&
      Object.prototype.hasOwnProperty.call(values, field.id));
    if (!selected.length) continue;
    const rows = prepareRows(doc, selected, values, columnWidth);
    activeGroup = String(group.label || "");

    if (activeGroup) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      const headingLines = wrap(doc, activeGroup, contentWidth - 6);
      const headingHeight = Math.max(GROUP_HEIGHT, 3 + headingLines.length * 4.8);
      const firstRowHeight = Math.max(...rows[0].map((cell) =>
        cellHeight(cell.labels.length, cell.lines.length)));
      const maxRowHeightOnFreshPage = pageBottom - TOP_MARGIN - headingHeight;
      const firstRowRequired = firstRowHeight > maxRowHeightOnFreshPage
        ? MIN_ROW_HEIGHT : firstRowHeight;
      // Não deixa o título do grupo sozinho no rodapé sem seus campos.
      if (y + headingHeight + firstRowRequired > pageBottom) {
        doc.addPage();
        y = TOP_MARGIN;
      }
      groupTitle(activeGroup);
    }
    rows.forEach(drawRow);
    y += GROUP_GAP;
  }

  activeGroup = "";
  const pages = doc.getNumberOfPages();
  for (let page = 1; page <= pages; page += 1) {
    doc.setPage(page);
    doc.setDrawColor(226, 232, 240);
    doc.line(MARGIN, pageHeight - 10, pageWidth - MARGIN, pageHeight - 10);
    doc.setTextColor(100, 116, 139);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text("Página " + page + " de " + pages,
      pageWidth - MARGIN, pageHeight - 6, { align: "right" });
  }
  return doc;
};

export const createRequestPdf = (options) =>
  renderRequestPdf(new jsPDF({ compress: true }), options);
